import { StepLoading } from "@/base/wizard/StepLoading";
import { AddressFieldset } from "@/components/AddressFieldset";
import { ExtractedField, ExtractedMarker } from "@/components/ExtractedReview";
import { NumericField } from "@/components/NumericField";
import { SelectField } from "@/components/SelectField";
import { TextField } from "@/components/TextField";
import { FIELD_ERROR } from "@/components/fieldStyles";
import { ClassCodeField } from "@/components/submissions/ClassCodeField";
import { AddFromRecord } from "@/components/submissions/wc/AddFromRecord";
import { StepLoadProblem } from "@/components/submissions/wc/StepLoadProblem";
import { THREE_COLUMNS, TWO_COLUMNS } from "@/components/submissions/wc/grids";
import { AddressReadOnly } from "@/components/submissions/wc/ReadOnlyParts";
import {
  RepeatableRows,
  SectionCard,
  ToggleSection,
  type WizardExtracted,
  type WizardFieldErrors,
  type WizardStepContext,
} from "@/base";
import { EMPTY_ADDRESS } from "@/lib/address";
import type { LocationsAsks } from "@/lib/submissions/wc/locations";
import type { SubmissionLocation } from "@/lib/submissions/brokerSubmissions";
import { insuredEditable } from "@/lib/submissions/wc/business";
import type { WcDraft } from "@/lib/submissions/wc/draft";
import {
  addressLabel,
  addressOf,
  buildingHasContent,
  CITY_LIMITS_OPTIONS,
  codeLocation,
  EMPTY_BUILDING,
  employeesAt,
  isBlankLocation,
  locationCandidates,
  locationFromAccount,
  MAILING_ELSEWHERE,
  mailingLocation,
  mailingOptions,
  newClassRow,
  newLocation,
  PREMISES_INTEREST_OPTIONS,
  type CheckLocation,
  type WcBuilding,
  type WcClassRow,
  type WcLocation,
  type WcMailing,
} from "@/lib/submissions/wc/locations";
import { newRowKey } from "@/lib/submissions/wc/rowKey";
import { useSubmissionLocations } from "@/lib/submissions/wc/useSubmissionLocations";

// The Workers' Comp wizard's Locations and payroll step (BRK-S302): where the
// business works, and each location's class codes with full-time, part-time
// and payroll, the way the ACORD 130 and Great American's application group
// them. The locations are the submission's own (PMS-S702), read when the step
// opens; one copied from the insured's record keeps its source, and the
// account's premises change only at bind (PMS-S713, Nav 2026-10-07). The
// mailing address is the insured's, editable only while the insured is still
// this submission's own (Nav, 2026-10-08). An uploaded ACORD packet fills the
// locations, their class codes and the mailing address, each marked until
// edited (BRK-S306). The locations sit in one white section card and the
// mailing address in another, as the account form's groups do (BRK-S349).
//
// BRK-S390 (Tyler's doc, Step 2; the 10/20 call): a line that doesn't ask
// building details, as Workers' Comp doesn't, shows no toggle for them, and
// what a location already holds is kept. The mailing address is any entered
// location's, location 1's by default, or a different one.

/** PMS refuses an address part by part; the step shows it on the address. */
const ADDRESS_FIELDS = [
  "address",
  "street",
  "addressLine2",
  "city",
  "state",
  "zip",
  "county",
];

/** PMS names the mailing address by the account's path
 *  (`mailingAddress.zip`); the step shows it on the mailing address
 *  (BRK-S339). */
const MAILING_PATH = /^mailingAddress(\.|$)/;

function mailingError(errors: WizardFieldErrors): string | undefined {
  return (
    errors.mailing ??
    Object.entries(errors).find(([path]) => MAILING_PATH.test(path))?.[1]
  );
}

const CHECK_LOCATION: Record<CheckLocation, string> = {
  saved:
    "Check the location: this row was saved without one, so it’s on location 1.",
  document:
    "Check the location: your document didn’t say which location this row is at, so it’s on location 1.",
};

const filledRow = (r: WcClassRow) => r.code !== null || r.payroll !== undefined;

export function LocationsStep({
  draft,
  setDraft,
  setLoaded,
  errors,
  busy,
  extracted,
  buildings,
}: WizardStepContext<WcDraft> & LocationsAsks) {
  const { problem, retry } = useSubmissionLocations(draft, setLoaded);

  if (problem) {
    return <StepLoadProblem problem={problem} onRetry={retry} />;
  }
  if (draft.savedLocations === null) {
    return <StepLoading label="Loading the locations…" />;
  }

  const saved = draft.savedLocations;
  const locations = draft.locations;
  const setLocations = (next: WcLocation[]) => {
    // A location removed takes its document marks with it.
    const kept = new Set(next.map((l) => l.key));
    for (const l of locations) {
      if (kept.has(l.key)) continue;
      for (const key of [
        l.key,
        `${l.key}.building`,
        ...l.rows.map((r) => r.key),
      ]) {
        extracted.touch(key);
      }
    }
    setDraft((d) => ({ ...d, locations: next }));
  };
  const candidates = locationCandidates(draft.account, locations);
  const submissionId = draft.submission?.id ?? "";
  // Each row's place in the payroll rows as written (`exposures.K`), where
  // PMS keys a refusal about one.
  const wireIndex = new Map<string, number>();
  locations.flatMap((l) => l.rows).forEach((r, k) => wireIndex.set(r.key, k));

  function addFromAccount(addressId: string) {
    const address = candidates.find((a) => a.id === addressId);
    if (!address) return;
    const copy = locationFromAccount(address, newRowKey());
    // An untouched location 1 is replaced, not left blank above the copy.
    setLocations(
      locations.length === 1 && isBlankLocation(locations[0])
        ? [copy]
        : [...locations, copy],
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SectionCard title="Locations and payroll" titleAs="h3">
        <div className="flex flex-col gap-4">
          <AddFromRecord
            label="Add an address from the insured’s record"
            placeholder="Choose an address"
            choices={candidates.map((a) => ({
              key: a.id,
              label: addressLabel(a),
            }))}
            onAdd={addFromAccount}
            disabled={busy}
          />
          <RepeatableRows
            label="Locations"
            headingLevel={4}
            rows={locations}
            onChange={setLocations}
            newRow={() => newLocation(newRowKey())}
            rowKey={(l) => l.key}
            itemLabel={(_l, i) => `Location ${i + 1}`}
            addLabel="Add a location"
            minRows={1}
            removePrompt={(l, i) =>
              l.rows.some(filledRow)
                ? `Remove Location ${i + 1} and its class codes?`
                : null
            }
            renderRow={(l, update, i) => (
              <LocationFields
                location={l}
                update={update}
                name={`Location ${i + 1}`}
                at={`locations.${i}`}
                saved={saved}
                submissionId={submissionId}
                wireIndex={wireIndex}
                errors={errors}
                busy={busy}
                extracted={extracted}
                buildings={buildings}
              />
            )}
          />
        </div>
      </SectionCard>
      <MailingAddress
        draft={draft}
        onChange={(mailing) => {
          extracted.touch("mailing");
          setDraft((d) => ({ ...d, mailing }));
        }}
        errors={errors}
        busy={busy}
        marked={extracted.isPending("mailing")}
      />
    </div>
  );
}

function LocationFields({
  location: l,
  update,
  name,
  at,
  saved,
  submissionId,
  wireIndex,
  errors,
  busy,
  extracted,
  buildings,
}: LocationsAsks & {
  location: WcLocation;
  update: (next: WcLocation) => void;
  name: string;
  /** Where the step keys this location's errors: `locations.N`. */
  at: string;
  saved: readonly SubmissionLocation[];
  submissionId: string;
  wireIndex: ReadonlyMap<string, number>;
  errors: WizardFieldErrors;
  busy: boolean;
  extracted: WizardExtracted;
}) {
  const set = (patch: Partial<WcLocation>) => update({ ...l, ...patch });
  // An uploaded document's marks: the address, and the building details.
  const buildingKey = `${l.key}.building`;
  const setBuilding = (patch: Partial<WcLocation>) => {
    extracted.touch(buildingKey);
    set(patch);
  };
  const locationError =
    errors[`${at}.id`] ?? errors[`${at}.sourceLocationId`] ?? errors[at];
  const addressError = ADDRESS_FIELDS.map((f) => errors[`${at}.${f}`]).find(
    Boolean,
  );
  const searchAt = codeLocation(l, saved);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {locationError && <p className={FIELD_ERROR}>{locationError}</p>}
      {l.sourceLocationId && (
        <p className="text-xs text-brk-dim">
          Copied from the insured’s record. Changes stay on this submission
          until the policy binds.
        </p>
      )}
      <ExtractedField marked={extracted.isPending(l.key)}>
        <AddressFieldset
          bare
          groupLabel={`${name} address`}
          value={l.address}
          onChange={(address) => {
            extracted.touch(l.key);
            set({ address });
          }}
          withCounty
          countyRequired
          withLine2
          error={addressError}
        />
      </ExtractedField>
      <RepeatableRows
        label={`Class codes at ${name}`}
        rows={l.rows}
        onChange={(rows) => {
          // A row removed takes its document mark with it.
          const kept = new Set(rows.map((r) => r.key));
          for (const r of l.rows) if (!kept.has(r.key)) extracted.touch(r.key);
          set({ rows });
        }}
        newRow={() => newClassRow(newRowKey())}
        rowKey={(r) => r.key}
        itemLabel={(_r, j) => `Class code ${j + 1}`}
        addLabel="Add a class code"
        minRows={1}
        headingLevel={5}
        renderRow={(r, updateRow, j) => (
          <ClassRowFields
            row={r}
            update={(next) => {
              extracted.touch(r.key);
              updateRow(next);
            }}
            marked={extracted.isPending(r.key)}
            at={`${at}.rows.${j}`}
            wireAt={`exposures.${wireIndex.get(r.key) ?? -1}`}
            searchAt={searchAt}
            submissionId={submissionId}
            errors={errors}
          />
        )}
      />
      <p className="text-body text-brk-text">
        Total employees:{" "}
        <span className="tabular-nums font-medium">{employeesAt(l)}</span>
      </p>
      {buildings && (
        <>
          <ToggleSection
            question="Add building details?"
            value={l.hasBuildingDetails}
            // No clears the details in the same update as the answer (the
            // toggle has already asked when there was anything to lose).
            onChange={(yes) =>
              setBuilding(
                yes
                  ? { hasBuildingDetails: true }
                  : {
                      hasBuildingDetails: false,
                      building: { ...EMPTY_BUILDING },
                    },
              )
            }
            hasContent={buildingHasContent(l.building)}
            clearPrompt={`Clear ${name}’s building details?`}
          >
            <BuildingFields
              building={l.building}
              update={(building) => setBuilding({ building })}
              at={at}
              errors={errors}
              busy={busy}
            />
          </ToggleSection>
          {extracted.isPending(buildingKey) && <ExtractedMarker />}
        </>
      )}
    </div>
  );
}

function ClassRowFields({
  row,
  update,
  marked,
  at,
  wireAt,
  searchAt,
  submissionId,
  errors,
}: {
  row: WcClassRow;
  update: (next: WcClassRow) => void;
  /** An uploaded document filled the row, and it is unconfirmed (BRK-S306). */
  marked: boolean;
  /** Where the step keys this row's errors: `locations.N.rows.M`. */
  at: string;
  /** Where PMS keys a refusal about it: `exposures.K`. */
  wireAt: string;
  searchAt: { id?: string; state: string } | undefined;
  submissionId: string;
  errors: WizardFieldErrors;
}) {
  const set = (patch: Partial<WcClassRow>) => update({ ...row, ...patch });
  const codeError = errors[`${at}.code`] ?? errors[`${wireAt}.codeId`];
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {row.checkLocation && (
        <p className="text-sm text-brk-text">
          {CHECK_LOCATION[row.checkLocation]}
        </p>
      )}
      {row.code === null && row.documentCode && (
        <p className="text-sm text-brk-text">
          Your document says class code {row.documentCode}. Choose the matching
          class code.
        </p>
      )}
      <ClassCodeField
        label="Class code"
        value={row.code}
        onChange={(code) => set({ code, checkLocation: undefined })}
        submissionId={submissionId}
        location={searchAt}
        required
        error={codeError}
      />
      <div className={THREE_COLUMNS}>
        <NumericField
          kind="number"
          label="Full-time employees"
          value={row.fullTime}
          onChange={(fullTime) => set({ fullTime })}
          min={0}
          error={errors[`${at}.fullTime`] ?? errors[`${wireAt}.fullTimeCount`]}
          required
        />
        <NumericField
          kind="number"
          label="Part-time employees"
          value={row.partTime}
          onChange={(partTime) => set({ partTime })}
          min={0}
          error={errors[`${at}.partTime`] ?? errors[`${wireAt}.partTimeCount`]}
          required
        />
        <NumericField
          kind="currency"
          label="Annual payroll"
          value={row.payroll}
          onChange={(payroll) => set({ payroll })}
          error={errors[`${at}.payroll`] ?? errors[`${wireAt}.payroll`]}
          required
        />
      </div>
      {marked && <ExtractedMarker />}
    </div>
  );
}

function BuildingFields({
  building: b,
  update,
  at,
  errors,
  busy,
}: {
  building: WcBuilding;
  update: (next: WcBuilding) => void;
  at: string;
  errors: WizardFieldErrors;
  busy: boolean;
}) {
  const set = (patch: Partial<WcBuilding>) => update({ ...b, ...patch });
  return (
    <div className={TWO_COLUMNS}>
      <TextField
        label="Building number"
        value={b.bldNumber}
        onChange={(e) => set({ bldNumber: e.target.value })}
        error={errors[`${at}.bldNumber`]}
        disabled={busy}
        optional
      />
      <NumericField
        kind="number"
        label="Year built"
        value={b.yearBuilt}
        onChange={(yearBuilt) => set({ yearBuilt })}
        min={1700}
        max={2100}
        error={errors[`${at}.yearBuilt`]}
        optional
      />
      <NumericField
        kind="number"
        label="Occupied area (sq ft)"
        value={b.occupiedArea}
        onChange={(occupiedArea) => set({ occupiedArea })}
        min={0}
        error={errors[`${at}.occupiedArea`]}
        optional
      />
      <SelectField
        label="Owner or tenant"
        options={PREMISES_INTEREST_OPTIONS}
        placeholder="Choose one"
        value={b.interest}
        onChange={(e) =>
          set({
            interest:
              PREMISES_INTEREST_OPTIONS.find((o) => o.value === e.target.value)
                ?.value ?? "",
          })
        }
        error={errors[`${at}.interest`]}
        disabled={busy}
        optional
      />
      <SelectField
        label="City limits"
        options={CITY_LIMITS_OPTIONS}
        placeholder="Choose one"
        value={b.cityLimits}
        onChange={(e) =>
          set({
            cityLimits:
              CITY_LIMITS_OPTIONS.find((o) => o.value === e.target.value)
                ?.value ?? "",
          })
        }
        error={errors[`${at}.cityLimits`]}
        disabled={busy}
        optional
      />
      <NumericField
        kind="number"
        label="Highest floor"
        value={b.highestFloor}
        onChange={(highestFloor) => set({ highestFloor })}
        min={1}
        max={200}
        error={errors[`${at}.highestFloor`]}
        optional
      />
    </div>
  );
}

/** The insured's mailing address, while the insured is still this
 *  submission's own: any entered location's, location 1's by default, or a
 *  different address (BRK-S390). Otherwise shown as it is on record, since
 *  changing it is an endorsement. */
function MailingAddress({
  draft,
  onChange,
  errors,
  busy,
  marked,
}: {
  draft: WcDraft;
  onChange: (mailing: WcMailing) => void;
  errors: WizardFieldErrors;
  busy: boolean;
  /** An uploaded document set it, and it is unconfirmed (BRK-S306). */
  marked: boolean;
}) {
  const held = draft.account?.mailingAddress ?? null;
  const editable = insuredEditable(draft.account, draft.submission);

  return (
    <SectionCard title="Mailing address" titleAs="h3">
      <div className="flex flex-col gap-3">
        {editable ? (
          <>
            <SelectField
              label="Mailing address"
              options={mailingOptions(draft.locations)}
              // The location it is, location 1's should the chosen one go.
              value={
                mailingLocation(draft.locations, draft.mailing)?.key ??
                MAILING_ELSEWHERE
              }
              onChange={(e) =>
                onChange(
                  e.target.value === MAILING_ELSEWHERE
                    ? {
                        location: null,
                        mailing: draft.mailing.mailing ?? {
                          ...EMPTY_ADDRESS,
                        },
                      }
                    : { location: e.target.value },
                )
              }
              disabled={busy}
              required
            />
            {draft.mailing.location === null && (
              <AddressFieldset
                bare
                groupLabel="Different mailing address"
                value={draft.mailing.mailing ?? EMPTY_ADDRESS}
                onChange={(mailing) => onChange({ location: null, mailing })}
                withCounty
                withLine2
                error={mailingError(errors)}
              />
            )}
            {marked && <ExtractedMarker />}
          </>
        ) : (
          <>
            <p className="text-body text-brk-dim">
              This insured is already on record. Its mailing address changes
              only through an endorsement.
            </p>
            {held ? (
              <AddressReadOnly address={addressOf(held)} />
            ) : (
              <p className="text-body text-brk-text">None on record</p>
            )}
          </>
        )}
      </div>
    </SectionCard>
  );
}
